import React, { useState, useEffect, useCallback } from "react";
import { exportXLSX } from "./exportXLSX";import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, TextField, CircularProgress, Alert,
  Chip, Breadcrumbs, Link, Tooltip, LinearProgress, Button, TableSortLabel,
  ToggleButton, ToggleButtonGroup
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GroupsIcon from "@mui/icons-material/Groups";
import SearchIcon from "@mui/icons-material/Search";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import MapIcon from "@mui/icons-material/Map";

const API = process.env.REACT_APP_API_URL || "";

const getEfectividadColor = (val, min, max) => {
  if (val == null || max === min) return "#9e9e9e";
  const ratio = (val - min) / (max - min);
  if (ratio >= 0.75) return "#2e7d32";
  if (ratio >= 0.5) return "#689f38";
  if (ratio >= 0.25) return "#f9a825";
  return "#c62828";
};

const getEfectividadBg = (val, min, max) => {
  if (val == null || max === min) return "#f5f5f5";
  const ratio = (val - min) / (max - min);
  if (ratio >= 0.75) return "#e8f5e9";
  if (ratio >= 0.5) return "#f1f8e9";
  if (ratio >= 0.25) return "#fffde7";
  return "#ffebee";
};

function exportCSV(rows, columns, labels, filename) {
  const header = labels.join(",");
  const csv = [header, ...rows.map(r => columns.map(c => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const num = (v) => v != null ? Number(v).toLocaleString("es-MX") : "0";
const pct = (v) => v != null ? `${Number(v).toFixed(1)}%` : "0%";

const headerSx = { fontWeight: 700, bgcolor: "#AF272F", color: "#fff" };
const sortSx = {"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"};

const ENTIDAD_NOMBRES = {1:"AGUASCALIENTES",2:"BAJA CALIFORNIA",3:"BAJA CALIFORNIA SUR",4:"CAMPECHE",5:"COAHUILA",6:"COLIMA",7:"CHIAPAS",8:"CHIHUAHUA",9:"CIUDAD DE MÉXICO",10:"DURANGO",11:"GUANAJUATO",12:"GUERRERO",13:"HIDALGO",14:"JALISCO",15:"MÉXICO",16:"MICHOACÁN",17:"MORELOS",18:"NAYARIT",19:"NUEVO LEÓN",20:"OAXACA",21:"PUEBLA",22:"QUERÉTARO",23:"QUINTANA ROO",24:"SAN LUIS POTOSÍ",25:"SINALOA",26:"SONORA",27:"TABASCO",28:"TAMAULIPAS",29:"TLAXCALA",30:"VERACRUZ",31:"YUCATÁN",32:"ZACATECAS"};

function ReportesEstructura({ token }) {
  const [view, setView] = useState("entidad"); // entidad | distrito | plaza | seccion
  const [data, setData] = useState([]);
  const [selectedEntidad, setSelectedEntidad] = useState(null);
  const [selectedDistrito, setSelectedDistrito] = useState(null);
  const [selectedPlaza, setSelectedPlaza] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("desc");
  const [highlightId, setHighlightId] = useState(null);
  const [modo, setModo] = useState("territorio"); // "territorio" | "distrital"

  // Perfil territorial del usuario
  const perfil = JSON.parse(localStorage.getItem("perfil_territorial") || "null");
  const roleCns = JSON.parse(localStorage.getItem("roles") || "[]");
  const soloEstructura = (roleCns.includes("reportes_estructura") || (perfil && ((perfil.cargo_id >= 3 && perfil.cargo_id <= 7) || perfil.cargo_id === 13))) && !roleCns.includes("compulsa") && !roleCns.includes("monitoreo") && !roleCns.includes("nacional");
  const tipoTerritorio = (soloEstructura && perfil?.afiliacion_tipo) ? perfil.afiliacion_tipo : "NACIONAL";
  const canSwitchMode = roleCns.includes("nacional") || roleCns.includes("compulsa");

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async (url) => {
    setLoading(true); setError("");
    try {
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setData(json.data || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    if (tipoTerritorio === "DISTRITAL" && perfil?.entidad && perfil?.distrito) {
      setView("plaza");
      setSelectedEntidad({ entidad: perfil.entidad, entidad_nombre: ENTIDAD_NOMBRES[perfil.entidad] || "" });
      setSelectedDistrito({ distrito_f: perfil.distrito });
      fetchData(`${API}/api/estructura/por-plaza?entidad=${perfil.entidad}&distrito_f=${perfil.distrito}`);
    } else if (tipoTerritorio === "ESTATAL" && perfil?.entidad) {
      setView("distrito");
      setSelectedEntidad({ entidad: perfil.entidad, entidad_nombre: ENTIDAD_NOMBRES[perfil.entidad] || "" });
      fetchData(`${API}/api/estructura/por-distrito?entidad=${perfil.entidad}`);
    } else if (tipoTerritorio === "CIRCUNSCRIPCIONAL" && perfil?.circunscripcion) {
      fetchData(`${API}/api/estructura/por-entidad?circunscripcion=${perfil.circunscripcion}`);
    } else if (tipoTerritorio === "REGIONAL" && perfil?.region_decimal) {
      fetchData(`${API}/api/estructura/por-entidad?region_decimal=${perfil.region_decimal}`);
    } else {
      fetchData(`${API}/api/estructura/por-entidad`);
    }
  }, [fetchData]);

  const handleEntidadClick = (row) => {
    setSelectedEntidad(row); setView("distrito"); setSearch("");
    fetchData(`${API}/api/estructura/por-distrito?entidad=${row.entidad}`);
  };
  const handleDistritoClick = (row) => {
    if (!selectedEntidad?.entidad && row.entidad) {
      setSelectedEntidad({ entidad: row.entidad, entidad_nombre: row.entidad_nombre });
    }
    setSelectedDistrito(row); setView("plaza"); setSearch("");
    fetchData(`${API}/api/estructura/por-plaza?entidad=${row.entidad || selectedEntidad.entidad}&distrito_f=${row.distrito_f}`);
  };
  // Nivel mínimo al que puede subir
  const nivelMinimo = tipoTerritorio === "DISTRITAL" ? "plaza" : tipoTerritorio === "ESTATAL" ? "distrito" : "entidad";

  const handleBack = () => {
    if (view === "seccion") {
      setView("plaza"); setSelectedPlaza(null); setSearch("");
      fetchData(`${API}/api/estructura/por-plaza?entidad=${selectedEntidad.entidad}&distrito_f=${selectedDistrito.distrito_f}`);
    }
    else if (view === "plaza" && nivelMinimo !== "plaza") {
      setView("distrito"); setSelectedDistrito(null); setSearch("");
      if (modo === "distrital") {
        setSelectedEntidad({ entidad: null, entidad_nombre: "Todos" });
        fetchData(`${API}/api/estructura/por-distrito`);
      } else if (selectedEntidad?.entidad) {
        fetchData(`${API}/api/estructura/por-distrito?entidad=${selectedEntidad.entidad}`);
      } else {
        fetchData(`${API}/api/estructura/por-distrito`);
      }
    }
    else if (view === "distrito" && nivelMinimo === "entidad") { setView("entidad"); setSelectedEntidad(null); setSearch(""); fetchData(`${API}/api/estructura/por-entidad`); }
  };
  const canGoBack = (view === "seccion") || (view === "plaza" && nivelMinimo !== "plaza") || (view === "distrito" && nivelMinimo === "entidad" && modo !== "distrital");
  const handleRefresh = () => {
    if (view === "entidad") fetchData(`${API}/api/estructura/por-entidad`);
    else if (view === "distrito") fetchData(`${API}/api/estructura/por-distrito?entidad=${selectedEntidad.entidad}`);
    else if (view === "plaza") fetchData(`${API}/api/estructura/por-plaza?entidad=${selectedEntidad.entidad}&distrito_f=${selectedDistrito.distrito_f}`);
    else fetchData(`${API}/api/estructura/por-seccion-plaza?plaza_id=${selectedPlaza.plaza_id}`);
  };

  const handleSort = (col) => { if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortBy(col); setSortDir("desc"); } };

  const filtered = data.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.entidad_nombre || "").toLowerCase().includes(s)
      || (r.distrito_f_nombre || "").toLowerCase().includes(s)
      || (r.nombre_completo || "").toLowerCase().includes(s)
      || (r.uid || "").toLowerCase().includes(s);
  }).sort((a, b) => {
    const av = a[sortBy], bv = b[sortBy];
    const an = Number(av), bn = Number(bv);
    const numeric = !isNaN(an) && !isNaN(bn);
    let cmp = numeric ? an - bn : String(av || "").localeCompare(String(bv || ""), "es");
    return sortDir === "desc" ? -cmp : cmp;
  });

  const dataConAfil = filtered.filter(r => Number(r.afiliaciones) > 0);
  const efectividades = dataConAfil.map(r => Number(r.efectividad || 0));
  const minEfect = efectividades.length ? Math.min(...efectividades) : 0;
  const maxEfect = efectividades.length ? Math.max(...efectividades) : 100;

  // KPIs
  const totalUsuarios = data.reduce((s, r) => s + Number(r.total_usuarios || 1), 0);
  const totalAfil = data.reduce((s, r) => s + Number(r.afiliaciones || 0), 0);
  const totalVerif = data.reduce((s, r) => s + Number(r.verificados || 0), 0);
  const totalImpr = data.reduce((s, r) => s + Number(r.credenciales_impresas || 0), 0);
  const totalEntr = data.reduce((s, r) => s + Number(r.credenciales_entregadas || 0), 0);
  const efGlobal = totalAfil > 0 ? ((totalVerif / totalAfil) * 100).toFixed(1) : 0;

  const entidadNombre = selectedEntidad?.entidad_nombre || (data.length > 0 ? data[0].entidad_nombre : "");
  const title = view === "entidad" ? "Actividad de Afiliación"
    : view === "distrito" ? (selectedEntidad?.entidad ? `Actividad de Afiliación — ${entidadNombre}` : "Actividad de Afiliación — Todos los Distritos")
    : view === "plaza" ? `Actividad de Afiliación — ${entidadNombre} — Dto. ${selectedDistrito?.distrito_f}`
    : `${selectedPlaza?.nombre_cot || selectedPlaza?.plaza} — Secciones`;

  const cols = view === "entidad"
    ? [{k:"entidad_nombre",l:"Entidad"},{k:"total_plazas",l:"Puestos",a:"right"},{k:"total_secciones",l:"Secciones",a:"right"},{k:"afiliaciones",l:"Registros",a:"right"},{k:"verificados",l:"Verificados",a:"right"},{k:"rechazados",l:"Rechazados",a:"right"},{k:"error",l:"Error",a:"right"},{k:"efectividad",l:"Efectividad",a:"center"},{k:"credenciales_impresas",l:"Cred. Impresas",a:"right"},{k:"credenciales_entregadas",l:"Cred. Entregadas",a:"right"}]
    : view === "distrito"
    ? [{k:"distrito_f",l:"Dto."},{k:"total_plazas",l:"Puestos",a:"right"},{k:"total_secciones",l:"Secciones",a:"right"},{k:"afiliaciones",l:"Registros",a:"right"},{k:"verificados",l:"Verificados",a:"right"},{k:"rechazados",l:"Rechazados",a:"right"},{k:"error",l:"Error",a:"right"},{k:"efectividad",l:"Efectividad",a:"center"},{k:"credenciales_impresas",l:"Cred. Impresas",a:"right"},{k:"credenciales_entregadas",l:"Cred. Entregadas",a:"right"}]
    : view === "plaza"
    ? [{k:"plaza",l:"Puesto"},{k:"nombre_cot",l:"COT"},{k:"telefono",l:"Teléfono"},{k:"fecha_ingreso",l:"Fecha Ingreso"},{k:"ocupado",l:"Ocupado",a:"center"},{k:"secciones_ruta",l:"Secciones",a:"right"},{k:"afiliaciones",l:"Registros",a:"right"},{k:"verificados",l:"Verificados",a:"right"},{k:"rechazados",l:"Rechazados",a:"right"},{k:"error",l:"Error",a:"right"},{k:"efectividad",l:"Efectividad",a:"center"},{k:"credenciales_impresas",l:"Cred. Impresas",a:"right"},{k:"credenciales_entregadas",l:"Cred. Entregadas",a:"right"}]
    : [{k:"seccion",l:"Sección"},{k:"afiliaciones",l:"Registros",a:"right"},{k:"verificados",l:"Verificados",a:"right"},{k:"rechazados",l:"Rechazados",a:"right"},{k:"error",l:"Error",a:"right"},{k:"efectividad",l:"Efectividad",a:"center"},{k:"credenciales_impresas",l:"Cred. Impresas",a:"right"},{k:"credenciales_entregadas",l:"Cred. Entregadas",a:"right"}];

  return (
    <Box sx={{ width: "100%", mx: "auto", px: { xs: 1, sm: 2, md: 3 }, py: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}>
        {canGoBack && <IconButton onClick={handleBack} sx={{ color: "#AF272F" }}><ArrowBackIcon /></IconButton>}
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" sx={{ color: "#AF272F", fontWeight: 700, fontSize: { xs: "1.2rem", sm: "1.4rem", md: "1.5rem" } }}>{title}</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <span role="img" aria-label="aviso" style={{ fontSize: "1.1rem" }}>⚠️</span>
            <Typography variant="body2" sx={{ color: "#e65100", fontWeight: 600 }}>Solo usuarios activos</Typography>
          </Box>
        </Box>
        <IconButton onClick={handleRefresh} title="Actualizar"><RefreshIcon /></IconButton>
        <Button size="small" startIcon={<DownloadIcon />} onClick={() => exportXLSX(filtered, cols, `estructura_${view}_${new Date().toISOString().slice(0,10)}`)} sx={{ color: "#AF272F" }}>Excel</Button>
      </Box>

      <Breadcrumbs sx={{ mb: 2 }}>
        {nivelMinimo === "entidad" && <Link underline="hover" sx={{ cursor: "pointer", fontWeight: view === "entidad" ? 700 : 400, color: view === "entidad" ? "#AF272F" : "inherit" }} onClick={() => { if (nivelMinimo === "entidad") { setView("entidad"); setSelectedEntidad(null); setSearch(""); fetchData(`${API}/api/estructura/por-entidad`); } }}>Entidades</Link>}
        {(view === "distrito" || view === "plaza" || view === "seccion") && <Link underline="hover" sx={{ cursor: "pointer", fontWeight: view === "distrito" ? 700 : 400, color: view === "distrito" ? "#AF272F" : "inherit" }} onClick={() => { if (nivelMinimo !== "distrito" && nivelMinimo !== "plaza") { setView("distrito"); setSelectedDistrito(null); setSearch(""); fetchData(`${API}/api/estructura/por-distrito?entidad=${selectedEntidad.entidad}`); } }}>{selectedEntidad?.entidad_nombre}</Link>}
        {(view === "plaza" || view === "seccion") && <Link underline="hover" sx={{ cursor: "pointer", fontWeight: view === "plaza" ? 700 : 400, color: view === "plaza" ? "#AF272F" : "inherit" }} onClick={() => { if (nivelMinimo !== "plaza") { setView("plaza"); setSelectedPlaza(null); setSearch(""); fetchData(`${API}/api/estructura/por-plaza?entidad=${selectedEntidad.entidad}&distrito_f=${selectedDistrito.distrito_f}`); } }}>Dto. {selectedDistrito?.distrito_f}</Link>}
        {view === "seccion" && <Typography color="#AF272F" fontWeight={700}>{selectedPlaza?.nombre_cot || selectedPlaza?.plaza}</Typography>}
      </Breadcrumbs>

      {/* KPIs */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <Paper sx={{ p: 1.5, flex: 1, minWidth: 120, textAlign: "center", borderTop: "3px solid #4a148c" }}>
          <Typography variant="h6" fontWeight={700}>{num(data.length)}</Typography>
          <Typography variant="caption" color="text.secondary">{view === "entidad" ? "Entidades" : view === "distrito" ? "Distritos" : view === "plaza" ? "COTs / Plazas" : "Secciones"}</Typography>
        </Paper>
        <Paper sx={{ p: 1.5, flex: 1, minWidth: 120, textAlign: "center", borderTop: "3px solid #1565c0" }}>
          <Typography variant="h6" fontWeight={700}>{num(totalAfil)}</Typography>
          <Typography variant="caption" color="text.secondary">Registros</Typography>
        </Paper>
        <Paper sx={{ p: 1.5, flex: 1, minWidth: 120, textAlign: "center", borderTop: "3px solid #6a1b9a" }}>
          <Typography variant="h6" fontWeight={700}>{num(totalVerif)}</Typography>
          <Typography variant="caption" color="text.secondary">Verificados</Typography>
        </Paper>
        <Paper sx={{ p: 1.5, flex: 1, minWidth: 120, textAlign: "center", borderTop: "3px solid #2e7d32" }}>
          <Typography variant="h6" fontWeight={700}>{efGlobal}%</Typography>
          <Typography variant="caption" color="text.secondary">Efectividad</Typography>
        </Paper>
        <Paper sx={{ p: 1.5, flex: 1, minWidth: 120, textAlign: "center", borderTop: "3px solid #e65100" }}>
          <Typography variant="h6" fontWeight={700}>{num(totalImpr)}</Typography>
          <Typography variant="caption" color="text.secondary">Cred. Impresas</Typography>
        </Paper>
        <Paper sx={{ p: 1.5, flex: 1, minWidth: 120, textAlign: "center", borderTop: "3px solid #00695c" }}>
          <Typography variant="h6" fontWeight={700}>{num(totalEntr)}</Typography>
          <Typography variant="caption" color="text.secondary">Cred. Entregadas</Typography>
        </Paper>
      </Box>

      {/* Mejor / Peor */}
      {view !== "entidad" && dataConAfil.length > 1 && (() => {
        const sorted = [...dataConAfil].sort((a, b) => Number(b.efectividad) - Number(a.efectividad));
        const top = sorted[0];
        const bottom = sorted[sorted.length - 1];
        if (top === bottom) return null;
        const topName = view === "seccion" ? `Sección ${top.seccion}` : view === "plaza" ? (top.nombre_cot || top.plaza) : top.nombre_completo || `Dto. ${top.distrito_f} - ${top.distrito_f_nombre}` || top.uid;
        const bottomName = view === "seccion" ? `Sección ${bottom.seccion}` : view === "plaza" ? (bottom.nombre_cot || bottom.plaza) : bottom.nombre_completo || `Dto. ${bottom.distrito_f} - ${bottom.distrito_f_nombre}` || bottom.uid;
        return (
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Paper sx={{ p: 1.5, flex: 1, minWidth: 200, display: "flex", alignItems: "center", gap: 1, borderLeft: "4px solid #2e7d32", cursor: "pointer", "&:hover": { bgcolor: "#e8f5e9" } }} onClick={() => { const id = top.uid || `${top.entidad}-${top.distrito_f}`; setHighlightId(id); setTimeout(() => { const el = document.getElementById(`row-${id}`); if(el) el.scrollIntoView({behavior:"smooth",block:"center"}); }, 100); }} title="Click para ir al registro">
              <EmojiEventsIcon sx={{ color: "#2e7d32" }} />
              <Box>
                <Typography variant="body2" fontWeight={700} sx={{ color: "#2e7d32" }}>Mejor: {topName}</Typography>
                <Typography variant="caption">{pct(top.efectividad)} efectividad ({num(top.afiliaciones)} reg.)</Typography>
              </Box>
            </Paper>
            <Paper sx={{ p: 1.5, flex: 1, minWidth: 200, display: "flex", alignItems: "center", gap: 1, borderLeft: "4px solid #c62828", cursor: "pointer", "&:hover": { bgcolor: "#ffebee" } }} onClick={() => { const id = bottom.uid || `${bottom.entidad}-${bottom.distrito_f}`; setHighlightId(id); setTimeout(() => { const el = document.getElementById(`row-${id}`); if(el) el.scrollIntoView({behavior:"smooth",block:"center"}); }, 100); }} title="Click para ir al registro">
              <EmojiEventsIcon sx={{ color: "#c62828" }} />
              <Box>
                <Typography variant="body2" fontWeight={700} sx={{ color: "#c62828" }}>Menor: {bottomName}</Typography>
                <Typography variant="caption">{pct(bottom.efectividad)} efectividad ({num(bottom.afiliaciones)} reg.)</Typography>
              </Box>
            </Paper>
          </Box>
        );
      })()}

      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <SearchIcon sx={{ color: "text.secondary" }} />
        <TextField size="small" placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)} sx={{ width: { xs: "100%", sm: 250, md: 300 } }} />
        {canSwitchMode && (
          <ToggleButtonGroup size="small" value={modo} exclusive onChange={(e, val) => {
            if (!val) return;
            setModo(val); setSearch(""); setSelectedEntidad(null); setSelectedDistrito(null);
            if (val === "distrital") { setView("distrito"); setSelectedEntidad({ entidad: null, entidad_nombre: "Todos" }); fetchData(`${API}/api/estructura/por-distrito`); }
            else { setView("entidad"); fetchData(`${API}/api/estructura/por-entidad`); }
          }}>
            <ToggleButton value="territorio" sx={{ textTransform: "none", fontSize: "0.75rem", color: modo === "territorio" ? "#fff" : "#AF272F", bgcolor: modo === "territorio" ? "#AF272F" : "transparent", borderColor: "#AF272F", "&.Mui-selected": { bgcolor: "#AF272F", color: "#fff", "&:hover": { bgcolor: "#8c1f26" } }, "&:hover": { bgcolor: "#fce4ec" } }}><AccountTreeIcon sx={{ fontSize: 18, mr: 0.5 }} />Por Entidad</ToggleButton>
            <ToggleButton value="distrital" sx={{ textTransform: "none", fontSize: "0.75rem", color: modo === "distrital" ? "#fff" : "#AF272F", bgcolor: modo === "distrital" ? "#AF272F" : "transparent", borderColor: "#AF272F", "&.Mui-selected": { bgcolor: "#AF272F", color: "#fff", "&:hover": { bgcolor: "#8c1f26" } }, "&:hover": { bgcolor: "#fce4ec" } }}><MapIcon sx={{ fontSize: 18, mr: 0.5 }} />Todos los Distritos</ToggleButton>
          </ToggleButtonGroup>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>{filtered.length} registros</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2, "& .MuiLinearProgress-bar": { bgcolor: "#AF272F" } }} />}

      <TableContainer component={Paper} sx={{ maxHeight: "65vh", overflowX: "auto", borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", pb: 2 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {cols.map(c => (
                <TableCell key={c.k} align={c.a || "left"} sx={{...headerSx, ...(c.k==="efectividad"?{minWidth:130}:{})}}>
                  <TableSortLabel active={sortBy===c.k} direction={sortBy===c.k?sortDir:"asc"} onClick={()=>handleSort(c.k)} sx={sortSx}>{c.l}</TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((row, i) => {
              const efVal = Number(row.efectividad || 0);
              const color = getEfectividadColor(efVal, minEfect, maxEfect);
              const bg = getEfectividadBg(efVal, minEfect, maxEfect);
              const clickable = view !== "seccion";
              const rowId = row.uid || `${row.entidad}-${row.distrito_f}`;
              return (
                <TableRow key={i} hover={clickable}
                  id={`row-${rowId}`}
                  onClick={() => { if (view === "entidad") handleEntidadClick(row); else if (view === "distrito") handleDistritoClick(row); else if (view === "plaza") { setSelectedPlaza(row); setView("seccion"); setSearch(""); fetchData(`${API}/api/estructura/por-seccion-plaza?plaza_id=${row.plaza_id}`); } }}
                  sx={{...(clickable ? { cursor: "pointer", "&:hover": { bgcolor: "#fce4ec" } } : {}), bgcolor: highlightId === rowId ? "#fff9c4" : "inherit", transition: "background 0.5s"}}>
                  {cols.map(c => (
                    <TableCell key={c.k} align={c.a || "left"}
                      sx={c.k === "efectividad" ? { bgcolor: bg } : c.k === "uid" ? { fontFamily: "monospace", fontSize: "0.8rem" } : {}}>
                      {c.k === "efectividad" ? (
                        <Chip label={pct(row[c.k])} size="small" sx={{ bgcolor: color, color: "#fff", fontWeight: 700, fontSize: "0.75rem", minWidth: 70 }} />
                      ) : c.k === "ocupado" ? (row[c.k] ? '✅' : '❌')
                      : ["afiliaciones","verificados","rechazados","error","total_plazas","total_secciones","secciones_ruta","credenciales_impresas","credenciales_entregadas"].includes(c.k)
                        ? num(row[c.k]) : row[c.k]}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={cols.length} align="center" sx={{ py: 4, color: "text.secondary" }}>Sin datos</TableCell></TableRow>
            )}
            {filtered.length > 0 && (
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                {cols.map(c => {
                  if (["entidad_nombre","distrito_f_nombre","plaza","nombre_cot","nombre_completo","telefono","fecha_ingreso"].includes(c.k)) return <TableCell key={c.k} sx={{ fontWeight: 700 }}>{["entidad_nombre","plaza","seccion"].includes(c.k) ? "TOTAL" : ""}</TableCell>;
                  if (["circunscripcion","distrito_f","seccion","ocupado","uid"].includes(c.k)) return <TableCell key={c.k} sx={{ fontWeight: 700 }}>{c.k === "seccion" ? "TOTAL" : ""}</TableCell>;
                  if (c.k === "efectividad") { const a = filtered.reduce((s,r) => s + Number(r.afiliaciones||r.afiliados||0), 0); const v = filtered.reduce((s,r) => s + Number(r.verificados||r.validados||0), 0); return <TableCell key={c.k} align="center" sx={{ fontWeight: 700 }}>{a > 0 ? (v/a*100).toFixed(1)+"%" : "0%"}</TableCell>; }
                  if (["afiliaciones","verificados","rechazados","error","total_usuarios","credenciales_impresas","credenciales_entregadas","credenciales_por_imprimir","impresas_propias","impresas_terceros","entregadas_propias","entregadas_terceros","secciones_ruta","convencidos"].includes(c.k)) return <TableCell key={c.k} align="right" sx={{ fontWeight: 700 }}>{num(filtered.reduce((s,r) => s + Number(r[c.k]||0), 0))}</TableCell>;
                  return <TableCell key={c.k}></TableCell>;
                })}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default ReportesEstructura;
